<script setup lang="ts">
import { computed, onMounted, reactive } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import ClientOnly from '@/components/SSR/ClientOnly.vue'
import xss from 'xss'
import { useHead } from '@unhead/vue'
import { useLocalizedRouter } from '@/composables/localizedRouter'
import ManualLocationModal from '@/pages/home/components/HeaderSection/ManualLocationModal/ManualLocationModal.vue'
import DefaultLayout from '@/layouts/default.vue'
import type { MarketingPage } from '@/http/models/MarketingPage'
import OfferingGrid from '@/pages/marketplace/redesign/Offering/OfferingGrid.vue'
import LocationSearch from '@/components/location-search/LocationSearch.vue'
import LoginLink from '@/pages/home/components/HeaderSection/LoginLink/LoginLink.vue'
import RecentAddresses from '@/pages/home/components/HeaderSection/RecentAddresses/RecentAddresses.vue'
import { LOGIN_MODAL } from '@/types/Modals'
import EstablishmentHeaderVisual
  from '@/pages/establishment/components/redesign/EstablishmentHeader/EstablishmentHeaderVisual.vue'
import Trustpilot from '@/components/Misc/Trustpilot.vue'
import BrandUsps from '@/pages/home/components/HeaderSection/BrandUsps/BrandUsps.vue'
import PageHeader from '@/components/Headers/PageHeader/PageHeader.vue'
import Headline from '@/components/partials/Headline.vue'
import Hr from '@/components/partials/Hr.vue'
import Container from '@/components/Structure/Container/Container.vue'
// import { useGa } from '@/composables/useGa'
import { useAuthStore } from '@/stores/useAuthStore'
import { useModalsStore } from '@/stores/useModalsStore'
import { useUserStore } from '@/stores/useUserStore'
import { useSessionStore } from '@/stores/useSessionStore'
import { storeToRefs } from "pinia"

const router = useRouter()
const {localizedRoute} = useLocalizedRouter()
const {t, locale} = useI18n()
const {addresses} = useUserStore()
const sessionStore = useSessionStore()
const {toggle} = useModalsStore()
const authStore = useAuthStore()

const props = defineProps<{
  page: MarketingPage,
  related: any
}>()

const state = reactive({
  manualLocationModalOpen: false,
})

const { isAuthenticated } = storeToRefs(authStore)
const relatedCategories = computed(() => props.related.data?.categories)
const relatedCities = computed(() => props.related.data?.cities)
const scrollToTop = () => window.scrollTo(0, 0)

const onSubmit = async ({location}) => {
  sessionStore.setLocation(location)

  await router.push(localizedRoute({name: 'marketplace'}))
}

function openLoginModal() {
  toggle(LOGIN_MODAL, true)
}

// const ga = useGa()

const onSelectLocation = async (location) => {
  sessionStore.setLocation(location)

  await router.push(localizedRoute({name: 'marketplace'}))

  // ga.trackAddressSelected()
}

useHead({
  title: props.page.meta?.title ? props.page.meta.title : props.page.content.title,
  description: props.page.meta.description,
})

onMounted(() => {
  // TODO: use useHead to mount the script
  const s = document.createElement('script')
  s.src = '//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js'
  s.setAttribute('hid', 'trustpilot')
  s.setAttribute('async', true)
  document.head.appendChild(s)
})
</script>

<template>
  <ManualLocationModal :open="state.manualLocationModalOpen"
                       @close="state.manualLocationModalOpen = false"
                       @submit="onSubmit"/>
  <DefaultLayout>
    <div class="marketing">
      <EstablishmentHeaderVisual :image="page.media.images.cover.large.url"/>
      <Container class="mb-15">
        <div class="row">
          <div class="col-lg-6 marketing__header" v-if="page.content">
            <PageHeader gradient
                        :class="[ 'marketing__header__slogan', {'hide-address-input' : page.hideAddressInput} ]">
              <!-- eslint-disable vue/no-v-html -->
              <span v-html="xss(page.content.title)"/>
              <!-- eslint-enable vue/no-v-html -->
            </PageHeader>

            <template v-if="!page.hideAddressInput">
              <Headline>
                <h2>{{ t('home_header.component_search_instructions.title') }}</h2>
                <p class="line-lg" v-if="page.content.subtitle">
                  {{ page.content.subtitle || t('category.subtitle') }}
                </p>
                <i18n-t keypath="category.subtitle"
                        tag="p"
                        class="line-lg"
                        scope="global"
                        v-else>
                  <template #category_count>
                    <span class="branded">
                      {{ t('category.category_count') }}
                    </span>
                  </template>
                </i18n-t>
              </Headline>

              <LocationSearch class="marketing__header__location-search"
                              @submit="onSubmit"
                              @manual-search="state.manualLocationModalOpen = true"/>

              <ClientOnly>
                <RecentAddresses :addresses="addresses"
                                 @select="onSelectLocation"
                                 v-if="isAuthenticated"/>

                <LoginLink class="marketing__header__login-link"
                           @login="openLoginModal"
                           v-else/>
              </ClientOnly>
              <BrandUsps class="marketing__header__usps"/>
            </template>
          </div>
        </div>

        <template v-if="!page.hideAddressInput">
          <Headline class="marketing__offering__title" v-if="page.content">
            <i18n-t keypath="marketing.best_in_location"
                    tag="h2"
                    scope="global">
              <template #best>
                <span class="branded">
                  {{ t('category.best') }}
                </span>
                <br>
              </template>
              <template #category>
                {{ page.content.restaurants }}
              </template>
              <template #city>
                {{ page.city?.name ?? 'Nederland' }}
              </template>
            </i18n-t>
          </Headline>
        </template>

        <OfferingGrid :items="page.establishments"/>

        <div class="mt-20 row row--lg center-sm">
          <div class="col-sm-7 content" v-if="page.content">
            <h2 class="mt-0">
              {{ page.content.content_title }}
            </h2>
            <Trustpilot class="my-2" style="width: 290px"/>
            <Hr/>
            <div class="text-center">
              <img class=""
                   :src="page.content.content_image_url"
                   :alt="page.content.content_image_alt"/>
            </div>
            <!-- eslint-disable vue/no-v-html -->
            <div class="line-lg" v-html="xss(page.content.content_text)"></div>
            <!-- eslint-enable vue/no-v-html -->
          </div>
        </div>

        <div class="mt-4 row center-sm">
          <div class="col-sm-7">
            <div class="category__cta">
              <h2 class="mt-0">
                <template v-if="page.category !== null && page.city === null">
                  {{ t('marketing.fancy_food_category', {category: page.category.name.toLowerCase()}) }}
                </template>
                <template v-else-if="page.city !== null && page.category === null">
                  {{ t('marketing.fancy_food_in_city', {city: page.city.name}) }}
                </template>
                <template v-else-if="page.category !== null && page.city !== null">
                  {{
                    t('marketing.search_title', {
                      category: page.category.name.toLowerCase(),
                      city: page.city.name,
                    })
                  }}
                </template>
                <template v-else>
                  {{ t('marketing.fancy_food') }}
                </template>
              </h2>
              <p class="mb-2">
                {{ t('category.search') }}
              </p>
              <LocationSearch class="marketing__header__location-search"
                              :disable-auto-focus="true"
                              @submit="onSubmit"
                              @manual-search="state.manualLocationModalOpen = true"/>
              <RecentAddresses v-if="isAuthenticated"
                               :addresses="addresses"
                               @select="onSelectLocation"/>
              <LoginLink v-if="!isAuthenticated"
                         class="marketing__header__login-link"
                         @login="openLoginModal"/>
            </div>

            <div v-if="relatedCities && relatedCities.length">
              <h2 class="mt-15">
                {{ t('marketing.order_also_city', {city: page.city?.name}) }}
              </h2>
              <ul class="row list__unstyled category__row">
                <li v-for="(city, index) in relatedCities" :key="index" class="col-sm-4">
                  <RouterLink :to="localizedRoute({ name: 'slug', params: { slug: city.slug[locale] || 'unknown' } })"
                              class="link"
                              @click="scrollToTop">
                    {{ city.category.name[locale] }}
                  </RouterLink>
                </li>
              </ul>
            </div>

            <div v-if="relatedCategories && relatedCategories.length">
              <h2 class="mt-15">
                {{ t('marketing.order_also_category', {category: page.category.name?.toLowerCase()}) }}
              </h2>
              <ul class="row list__unstyled category__row">
                <li v-for="(category, index) in relatedCategories" class="col-sm-4" :key="index">
                  <RouterLink :to="localizedRoute({ name: 'slug', params: { slug: category.slug[locale] || 'unknown' } })"
                              class="link"
                              @click="scrollToTop">
                    {{ category.city?.name }}
                  </RouterLink>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </Container>
    </div>
  </DefaultLayout>
</template>

<style lang="scss" scoped>
@import "@/assets/css/mixins/breakpoints-down.scss";
@import "@/assets/css/mixins/breakpoints-up.scss";
@import "@/assets/css/mixins/styling.scss";

.container {
  margin: auto 1.5rem;

  @include sm-up {
    margin: auto auto;
  }
}

.marketing {
  &__header {
    &__usps {
      margin-top: 5rem;
    }

    .hide-address-input {
      margin-bottom: 3rem;
    }

    &__slogan {
      margin-top: 3rem;
      text-align: center;

      @include lg-up {
        margin-top: 13rem;
        text-align: left;
      }
    }

    &__location-search {
      max-width: 32rem;
      margin:    1.5rem auto 0;

      @include lg-up {
        margin: 1.5rem 0 0;
      }
    }

    &__login-link {
      margin-left: 0;
    }
  }

  &__offering {
    &__title {
      margin-top: 10rem;
    }
  }
}

.category {
  &__header {
    position:        relative;
    display:         flex;
    justify-content: center;
    flex-direction:  column;
    margin:          -35% 0 5rem 0;

    @include lg-down {
      background: white;
      padding:    1.5rem;
      margin-top: 7rem;
      @include radius;
    }

    @include lg-up {
      margin:     5rem 0 0 0;
      min-height: 90vh;
    }
  }

  &__cta {
    background: white;
    @include radius;
    @include shadow-sm;
    padding:    2rem;
  }

  &__row {
    @include sm-up {
      margin-left:  -0.75rem;
      margin-right: -0.75rem;
    }
  }
}
</style>
